import { useApolloClient } from '@apollo/client';
import { type PropsWithChildren, useContext, useEffect } from 'react';
import { IntlProvider } from 'react-intl';

import type { Country, Locale } from '@/types/regionTypes';

import { LocaleConfiguration } from '@/lib/configuration/configuration';
import {
  GLOBAL_E_MARKET_AVAILABLE,
  IMPERIAL_COUNTRIES,
  TAX_EXCLUDED_COUNTRIES,
  WORLD_WIDE_CATALOG_COUNTRY,
} from '@/lib/const';
import type { LocaleMessages } from '@/lib/intl';
import {
  formatAsDate,
  formatAsDateTime,
  formatAsPrice,
  formatAsPriceString,
} from '@/lib/intl';
import { DEFAULT_COUNTRY, getRegionFromLocale } from '@/lib/regions'; // Saleor channel
import { StorageHelper } from '@/lib/storage';
import { LocaleContext } from '@/providers/LocaleProvider';
import { checkoutActions } from '@/states/checkout';

import type { RegionsProviderContext } from './RegionProviderContext';
import { Provider } from './RegionProviderContext';

type RegionProviderProps = PropsWithChildren & {
  messages: LocaleMessages;
};

export const RegionProvider = ({ children, messages }: RegionProviderProps) => {
  const routerLocale = useContext(LocaleContext);
  const { country, locale } = getRegionFromLocale(routerLocale);
  const apolloClient = useApolloClient();

  const supportedCountries =
    LocaleConfiguration.getInstance().getSupportedCountries();

  const storageHelper = new StorageHelper();
  const localeChanged = storageHelper.getCookie('LocaleChanged');
  useEffect(() => {
    const checkForLocaleChange = async () => {
      if (localeChanged) {
        const cookies = storageHelper.getCookies();

        const { country: newCountry, locale: newLocale } = getRegionFromLocale(
          `${cookies['user_preferred_country']}-${cookies['user_preferred_locale']}`,
        );
        await resetConfig(newCountry, newLocale);
        storageHelper.removeCookie('LocaleChanged');
      }
    };

    checkForLocaleChange();
  }, [localeChanged]);

  const resetConfig = async (newCountry: Country, newLocale: Locale) => {
    //save country and locale and isManually
    const storageHelper = new StorageHelper();
    storageHelper.setCookie('user_preferred_country', `${newCountry.slug}`, {
      expires: 400, // chrome,firefox 400days, safari 7days
    });
    storageHelper.setCookie('user_preferred_locale', `${newLocale.slug}`, {
      expires: 400,
    });
    new StorageHelper().setCookie('user_preferred_defined', 1);
    if (
      GLOBAL_E_MARKET_AVAILABLE?.includes(
        `${newCountry.slug}-${newLocale.slug}`,
      )
    ) {
      const domain =
        typeof window !== 'undefined'
          ? `.${window.location.hostname}`
          : undefined;

      storageHelper.setCookie(
        'GlobalE_Data',
        JSON.stringify({
          countryISO: newCountry.code,
          currencyCode: newCountry.currency,
          cultureCode: null,
        }),
        { domain },
      );
      storageHelper.setCookie('GlobalE_Locale', `${newLocale.locale}`, {
        domain,
      });
    }

    // We clear the checkout state when the locale is changed
    await checkoutActions.clear();

    //TODO if apollo still in requesting , resetStore will throw error
    apolloClient.stop();
    await apolloClient.resetStore();
  };

  const jumpToNewLocale = (countrySlug: string, languageSlug: string) => {
    // Here we need to redirect with window instead of push because of the issue with algolia search
    const currentUrl = new URL(window.location.href);
    const currentUrlPathname = currentUrl.pathname;
    const currentUrlQuery = currentUrl.search;

    const pathSegments = currentUrlPathname
      .split('/')
      .filter((segment) => segment);

    pathSegments[0] = `${countrySlug}-${languageSlug}`;
    const newPath = `/${pathSegments.join('/')}`;

    window.location.href = `${newPath}${currentUrlQuery}`;
  };

  /**
   *
   * @param code Country.slug
   * @param locale Locale.slug
   * @returns
   */
  const changeCountry = async (code: string, locale: string) => {
    const storageHelper = new StorageHelper();
    const currentCountry = storageHelper.getCookie('user_preferred_country');
    const newCountry = supportedCountries.find(
      ({ slug: countryCode }) => code === countryCode,
    )!;
    if (!newCountry) {
      jumpToNewLocale(code, locale);
      return;
    }
    const newLocale = newCountry.locales.find(
      ({ slug: localeCode }) =>
        (locale || newCountry.locales[0].code) === localeCode,
    )!;
    if (!newLocale) {
      jumpToNewLocale(code, locale);
      return;
    }
    if (newCountry.slug !== currentCountry) {
      await resetConfig(newCountry, newLocale);
    }

    jumpToNewLocale(newCountry.slug, newLocale.slug);
  };

  const formatPrice: RegionsProviderContext['formatPrice'] = (price, opts?) =>
    formatAsPrice(
      price?.amount || 0,
      price?.currency || country.currency,
      country.defaultLocale,
      opts,
    );

  const formatPriceString: RegionsProviderContext['formatPriceString'] = (
    price,
    opts?,
  ) =>
    formatAsPriceString(
      price?.amount ?? 0,
      price?.currency ?? country.currency,
      country.defaultLocale,
      opts,
    );

  const formatDateTime = (dateTime: string) =>
    formatAsDateTime(dateTime, country.defaultLocale);

  const formatDate = (date: string) =>
    formatAsDate(date, country.defaultLocale);
  const myCustomErrorFunction = () => {
    //TODO handle error
    // console.log(e);
  };

  return (
    <Provider
      value={{
        locale,
        country,
        formatPrice,
        formatPriceString,
        formatDateTime,
        formatDate,
        changeCountry,
        supportedCountries: supportedCountries,
        query: {
          channel: country.channelSlug,
          languageCode: locale.code,
        },
        shouldIncludeVAT: !TAX_EXCLUDED_COUNTRIES?.includes(country.code),
        unit: IMPERIAL_COUNTRIES?.includes(country.code) ? 'inch' : 'mm',
        productCatalogMode: WORLD_WIDE_CATALOG_COUNTRY?.includes(country.code),
      }}
    >
      <IntlProvider
        defaultLocale={DEFAULT_COUNTRY.locales[0].locale}
        locale={locale.locale}
        messages={messages}
        onError={myCustomErrorFunction}
      >
        {children}
      </IntlProvider>
    </Provider>
  );
};
